import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useSelector, useDispatch, useLogger } from '~Hooks/index';
import OrderDisplay from './OrderDisplay';
import * as actions from '~actions';
import { CreateOrderEasyId, CreateOrderEasyIdVariables } from './__queries__';
import { useCart } from '~Components/CartProvider';

const CREATE_ID_MUTATION = gql`
  mutation CreateOrderEasyId($venueId: String!) {
    createOrderEasyId(venueId: $venueId) {
      orderEasyId
      success
    }
  }
`;

const Wrapper = styled.div``;

interface Props {
  venueId: string;
  orderId: string;
  visible: boolean;
  reset: () => void;
  setOrderEasyId?: (orderEasyId: string) => void;
  orderEasyId?: string | null;
}

const OrderIdManager: React.FunctionComponent<Props> = ({
  venueId,
  orderId,
  visible,
  reset,
  orderEasyId,
  setOrderEasyId,
}) => {
  const logger = useLogger('order-id-manager');
  const dispatch = useDispatch();

  const bongPrintingHalted = useSelector((state) => state.BongPrintingHalted);
  const order = useSelector((state) => state.POSOrders.find((o) => o.id === orderId));
  const payment = useSelector((state) => state.POSPayments.find((p) => p.order === orderId));
  const splitOrder = useSelector((state) => state.POSSplitOrder);

  const [createOrderEasyId, { data, loading }] = useMutation<
    CreateOrderEasyId,
    CreateOrderEasyIdVariables
  >(CREATE_ID_MUTATION);

  const [doneTimeout, setDoneTimeout] = useState<boolean>(false);

  const instantOrder = false; // cartItems.every((item) => Boolean(item.instant));

  // Creates and sets orderNumber for order, or sets it to empty string to move on with the order
  useEffect(() => {
    if (order?.orderNumber || loading) {
      return;
    }

    let orderNumber: string | undefined;

    // If we have a stored easyId, use it else create a new one
    if (orderEasyId) {
      orderNumber = orderEasyId;

      if (order) {
        dispatch(actions.updateOrder(orderId, { orderNumber }));
      }

      return;
    }

    createOrderEasyId({
      variables: {
        venueId: venueId,
      },
      fetchPolicy: 'no-cache',
    })
      .then(({ data }) => {
        orderNumber = data?.createOrderEasyId.orderEasyId;

        // Is collecting items on order
        if (bongPrintingHalted || !order) {
          // Store easyId
          orderNumber && setOrderEasyId && setOrderEasyId(orderNumber);
        }

        if (order) {
          dispatch(actions.updateOrder(orderId, { orderNumber }));
        }
      })
      .catch((e) => {
        logger.error('Failed to create order easy id', { errorObject: e });

        if (order) {
          dispatch(actions.updateOrder(orderId, { orderNumber: '' }));
        }
      });
  }, [orderEasyId, orderId, instantOrder, order?.orderNumber]);

  useEffect(() => {
    const resetScreen = () => {
      if (payment) {
        dispatch({
          type: 'REMOVE_POS_PAYMENT',
          id: payment.id,
        });
      }
      dispatch({
        type: 'REMOVE_POS_ORDER',
        id: orderId,
      });
      reset();
    };

    if (payment?.status === 'COMPLETED' && !splitOrder) {
      const id = setTimeout(() => {
        resetScreen();
      }, 10000);
      return () => clearTimeout(id);
    }

    if (doneTimeout) {
      resetScreen();
      visible = false;
    }

    return () => {};
  }, [payment && payment.status, doneTimeout]);

  const content = () => {
    /* if (instantOrder) {
      return <InstantOrder done={() => setDoneTimeout(true)} />;
    } */
    return (
      <OrderDisplay
        done={() => setDoneTimeout(true)}
        orderNumber={data && data.createOrderEasyId.orderEasyId}
      />
    );
  };

  return visible ? <Wrapper>{content()}</Wrapper> : null;
};

export default OrderIdManager;
